// src/services/authService.js
import { 
  LOGIN_API_URL, 
  REGISTER_API_URL, 
  PROFILE_API_URL,
  LOGOUT_API_URL,
  STORAGE_KEYS 
} from "../helpers/Constant";
import { getDeviceIdSync } from "../Utils/deviceId";

export const authService = {
  // Get auth headers for API calls
  getAuthHeaders: () => {
    const token = authService.getToken();
    if (!token) {
      console.warn("⚠️ No token found for auth headers");
      return null;
    }
    return {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-Device-ID": getDeviceIdSync(),
    };
  },

  // Login user
  login: async (email, password, role = 2) => {
    try {
      console.log("📡 Attempting login for:", email);
      
      const response = await fetch(LOGIN_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-Device-ID": getDeviceIdSync(),
        },
        body: JSON.stringify({ email, password, role }),
      });

      const result = await response.json();
      console.log("📡 Login API Response:", result);

      // Check for successful login (status_code === 200)
      if (result.status_code === 200 && result.data) {
        const userData = result.data; // contains token, refresh_token, id, etc.
        const token = userData.token;

        if (!token) {
          console.warn("⚠️ Login successful but no token found");
          return {
            success: false,
            error: "No token received from server."
          };
        }

        console.log("✅ Login successful, token stored");
        localStorage.setItem(STORAGE_KEYS.TOKEN, token);
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));

        return {
          success: true,
          user: userData,
          token: token,
        };
      }

      // Handle 422 validation errors
      if (response.status === 422) {
        let errorMessage = "Validation failed";
        if (result.message) {
          if (typeof result.message === 'object') {
            const errors = Object.values(result.message).flat();
            errorMessage = errors.join(', ');
          } else {
            errorMessage = result.message;
          }
        }
        return { success: false, error: errorMessage };
      }

      throw new Error(result.message || "Login failed");
    } catch (error) {
      console.error("❌ Login error:", error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Register user
  register: async (userData) => {
    try {
      console.log("📡 Attempting registration");
      
      const response = await fetch(REGISTER_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-Device-ID": getDeviceIdSync(),
        },
        body: JSON.stringify(userData),
      });

      const result = await response.json();
      console.log("📡 Register API Response:", result);

      if (result.status_code === 200 && result.data) {
        const userInfo = result.data; // contains token, etc.
        const token = userInfo.token;

        if (token) {
          console.log("✅ Registration successful, token stored");
          localStorage.setItem(STORAGE_KEYS.TOKEN, token);
        }
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userInfo));

        return {
          success: true,
          user: userInfo,
          token: token,
        };
      }

      // Handle 422 validation errors
      if (response.status === 422) {
        let errorMessage = "Validation failed";
        if (result.message) {
          if (typeof result.message === 'object') {
            const errors = Object.values(result.message).flat();
            errorMessage = errors.join(', ');
          } else {
            errorMessage = result.message;
          }
        }
        return { success: false, error: errorMessage };
      }

      throw new Error(result.message || "Registration failed");
    } catch (error) {
      console.error("❌ Registration error:", error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Get user profile
  getProfile: async (token) => {
    try {
      const authToken = token || authService.getToken();
      if (!authToken) {
        throw new Error("No authentication token");
      }

      console.log("📡 Fetching user profile");
      
      const response = await fetch(PROFILE_API_URL, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${authToken}`,
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-Device-ID": getDeviceIdSync(),
        },
      });

      const result = await response.json();
      console.log("📡 Profile API Response:", result);

      if (result.status_code === 200 && result.data) {
        const userData = result.data;
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
        return {
          success: true,
          user: userData
        };
      }

      if (response.status === 401) {
        authService.clearAuth();
        return {
          success: false,
          error: "UNAUTHORIZED",
          message: "Please login again"
        };
      }

      throw new Error(result.message || "Failed to fetch profile");
    } catch (error) {
      console.error("❌ Get profile error:", error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Update user profile
  updateProfile: async (userData, token) => {
    try {
      const authToken = token || authService.getToken();
      if (!authToken) {
        throw new Error("No authentication token");
      }

      console.log("📡 Updating user profile");
      
      const response = await fetch(PROFILE_API_URL, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${authToken}`,
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-Device-ID": getDeviceIdSync(),
        },
        body: JSON.stringify(userData),
      });

      const result = await response.json();
      console.log("📡 Update Profile API Response:", result);

      if (result.status_code === 200 && result.data) {
        const updatedUser = result.data;
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updatedUser));
        return {
          success: true,
          user: updatedUser
        };
      }

      if (response.status === 401) {
        authService.clearAuth();
        return {
          success: false,
          error: "UNAUTHORIZED",
          message: "Please login again"
        };
      }

      throw new Error(result.message || "Failed to update profile");
    } catch (error) {
      console.error("❌ Update profile error:", error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Logout user
  logout: async (token) => {
    try {
      const authToken = token || authService.getToken();
      if (authToken) {
        console.log("📡 Logging out user");
        await fetch(LOGOUT_API_URL, {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json",
            "X-Device-ID": getDeviceIdSync(),
          },
        });
      }
    } catch (error) {
      console.error("❌ Logout error:", error);
    } finally {
      authService.clearAuth();
      console.log("🧹 Auth data cleared");
    }
  },

  // Clear all auth data
  clearAuth: () => {
    try {
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      console.log("🧹 Auth data cleared");
    } catch (error) {
      console.error("❌ Error clearing auth data:", error);
    }
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    return !!localStorage.getItem(STORAGE_KEYS.TOKEN);
  },

  // Get current user from localStorage
  getCurrentUser: () => {
    const userStr = localStorage.getItem(STORAGE_KEYS.USER);
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (e) {
        return null;
      }
    }
    return null;
  },

  // Get token from localStorage
  getToken: () => {
    return localStorage.getItem(STORAGE_KEYS.TOKEN);
  },

  // Set token in localStorage
  setToken: (token) => {
    try {
      console.log("💾 Setting token:", token ? "Yes (length: " + token.length + ")" : "No");
      localStorage.setItem(STORAGE_KEYS.TOKEN, token);
    } catch (error) {
      console.error("❌ Error setting token:", error);
    }
  },

  // Refresh token (if your API supports it)
  refreshToken: async () => {
    try {
      const token = authService.getToken();
      if (!token) {
        console.warn("⚠️ No token to refresh");
        return { success: false, error: "No token" };
      }

      console.log("🔄 Attempting to refresh token");
      
      const response = await fetch(`${PROFILE_API_URL}/refresh`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-Device-ID": getDeviceIdSync(),
        },
      });

      if (response.status === 404) {
        console.log("ℹ️ Refresh endpoint not available");
        return { success: false, error: "Refresh endpoint not available" };
      }

      const result = await response.json();
      console.log("📡 Refresh token response:", result);

      if (result.status_code === 200 && result.data && result.data.token) {
        const newToken = result.data.token;
        authService.setToken(newToken);
        // Update user data if returned
        if (result.data) {
          localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(result.data));
        }
        console.log("✅ Token refreshed successfully");
        return { success: true, token: newToken };
      }

      if (response.status === 401) {
        console.log("⚠️ Token refresh failed - unauthorized");
        authService.clearAuth();
        return { success: false, error: "UNAUTHORIZED" };
      }

      return { success: false, error: result.message || "Failed to refresh token" };
    } catch (error) {
      console.error("❌ Refresh token error:", error);
      return { success: false, error: error.message };
    }
  },

  // Validate token with server
  validateToken: async () => {
    try {
      const token = authService.getToken();
      if (!token) {
        return { valid: false };
      }

      console.log("🔍 Validating token...");
      
      const response = await fetch(PROFILE_API_URL, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-Device-ID": getDeviceIdSync(),
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status_code === 200 && data.data) {
          console.log("✅ Token is valid");
          // Update stored user data
          localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data.data));
          return { valid: true, user: data.data };
        }
      }

      if (response.status === 401) {
        console.log("⚠️ Token is invalid or expired");
        authService.clearAuth();
        return { valid: false };
      }

      console.log("⚠️ Token validation failed");
      return { valid: false };
    } catch (error) {
      console.error("❌ Validate token error:", error);
      return { valid: false };
    }
  },

  // Helper to get device ID
  getDeviceId: () => getDeviceIdSync(),
};